<template>
	<div class="uploader">
		<div
			v-show="!showImage"
			:style="getPreviewImageSize"
			class="uploader__upload-button"
		>
			<input
				ref="upload"
				type="file"
				accept="image/*"
				class="uploader__input"
				title="이미지 등록"
				@change="onImageLoad"
			/>
		</div>
		<div
			v-show="showImage"
			:style="getPreviewImageSize"
			class="uploader__preview"
		>
			<img
				v-if="!byUrl"
				:src="previewImage.src"
				alt="업로드 이미지 미리보기"
				class="uploader__preview__img"
				@load="checkImageSize"
				@click="toggleOriginSizeImage"
			/>
			<img
				v-else
				:src="previewImage.src"
				alt="업로드 이미지 미리보기"
				class="uploader__preview__img"
				@load="setImageMeta"
				@error="fireError(`이미지 로드중 문제가 발생했습니다.`)"
				@click="toggleOriginSizeImage"
			/>
			<button
				type="button"
				class="uploader__preview__cancel"
				@click="cancelImage"
			>
				취소
			</button>
		</div>
		<transition name="fade">
			<div v-if="showOriginSize" class="uploader__origin-size origin">
				<div
					class="origin__background"
					@click="toggleOriginSizeImage"
				/>
				<div
					class="origin__image-wrapper"
					:style="{
						width: `${previewImage.originalWidth}px`,
						height: `${Math.min(windowHeight, previewImage.originalHeight)}px`,
					}"
				>
					<img
						:src="previewImage.src"
						alt="업로드 이미지 원래 사이즈로 보기"
						class="origin__image"
					/>
				</div>
			</div>
		</transition>
	</div>
</template>
<script>
	import {
		imageUpload,
		imageUploadWithSign,
		spinnerActive,
		spinnerInactive,
	} from '@/util';

	export default {
		name: 'ImageUploader',
		props: {
			group: {
				type: String,
				required: true,
			},
			imageSrc: {
				type: String,
				default: undefined,
			},
			imageSizeLimit: {
				type: Object,
				default() {
					return {
						width: 0,
						height: 0,
						previewWidth: 0,
						type: 'max',
					};
				},
			},
			fileSizeLimit: {
				type: Number,
				default: 0,
			},
			filePathHasSlash: {
				type: Boolean,
				default: true,
			},
			limit: {
				type: Boolean,
				default: true,
			},
		},
		data() {
			return {
				previewImage: {
					src: null,
					fileObject: null,
					fileName: null,
					fileType: null,
					extension: null,
					size: 0,
					originalWidth: 0,
					originalHeight: 0,
				},
				showImage: false,
				showOriginSize: false,
				isUploaded: false,
				requestUrl: null,
				preSign: null,
				preSignExpireAt: null,
				key: null,
				byUrl: false,
				windowHeight: window.innerHeight,
			};
		},
		watch: {
			imageSrc: {
				immediate: true,
				handler(value) {
					if (value && !this.previewImage.src) {
						this.setImageByUrl(value);
					}
				},
			},
		},
		computed: {
			getFileSizeLimit() {
				return this.fileSizeLimit * 1024 * 1024;
			},
			getPreviewImageSize() {
				const imageSize = this.imageSizeLimit;
				let [width, height] = ['100px', '100%'];

				if (imageSize.width !== 0 && imageSize.height !== 0) {
					width = imageSize.previewWidth
						? `${imageSize.previewWidth}px`
						: `${Math.min(imageSize.width, 100)}px`;
					height = imageSize.previewWidth
						? `${Math.floor((imageSize.previewWidth / imageSize.width) * imageSize.height)}px`
						: `${Math.floor((parseInt(width, 10) / imageSize.width) * imageSize.height)}px`;
				}

				return { width, height };
			},
		},
		methods: {
			toggleOriginSizeImage() {
				this.showOriginSize = !this.showOriginSize;
			},
			onImageLoad(event) {
				const image = event.target.files[0];

				if (!image) {
					return false;
				}

				if (
					this.fileSizeLimit > 0 &&
					image.size > this.getFileSizeLimit
				) {
					this.fireError(
						`이미지는 ${this.fileSizeLimit}MB까지 등록이 가능 합니다.`
					);
					return false;
				}

				const reader = new FileReader();

				reader.readAsDataURL(image);

				reader.onload = event => {
					this.cancelImage();
					this.setImage(event, image);
				};
			},
			setImage(event, image) {
				this.previewImage = {
					src: event.target.result,
					size: event.total,
					fileObject: image,
					fileType: image.type,
					extension: image.type.split('/').pop(),
					fileName: image.name,
					originalWidth: 0,
					originalHeight: 0,
				};
			},
			setImageByUrl(url) {
				this.byUrl = true;
				this.previewImage.src = url;
				this.key = this.previewImage.src
					.split(this.$store.state.env.IMAGE_STORAGE_PATH)
					.pop();
			},
			cancelImage() {
				if (this.key) {
					this.$emit('cancel', this.key);
				}

				this.$refs.upload.value = null;
				this.previewImage = {
					src: null,
					fileObject: null,
					fileName: null,
					fileType: null,
					extension: null,
					size: 0,
					originalWidth: 0,
					originalHeight: 0,
				};
				this.key = null;
				this.showImage = false;
				this.byUrl = false;

				this.$emit('upload', {});
			},
			checkImageSize(event) {
				const width = event.target.naturalWidth;
				const height = event.target.naturalHeight;
				const limitWidth = this.imageSizeLimit.width;
				const limitHeight = this.imageSizeLimit.height;
				const widthRatio = this.imageSizeLimit.widthRatio;
				const heightRatio = this.imageSizeLimit.heightRatio;
				const type = this.imageSizeLimit.type;
				const errors = [];
				const current = new Date().getTime();

				if (this.limit === true) {
					if (limitWidth !== 0) {
						if (type === 'fit' && width !== limitWidth) {
							errors.push(
								`이미지 가로 사이즈가 ${limitWidth}px 이 아닙니다.`
							);
						} else if (type === 'max' && width > limitWidth) {
							errors.push(
								`이미지 가로 사이즈가 ${limitWidth}px 보다 큽니다.`
							);
						}
					}

					if (limitHeight !== 0) {
						if (type === 'fit' && height !== limitHeight) {
							errors.push(
								`이미지 세로 사이즈가 ${limitHeight}px 이 아닙니다.`
							);
						} else if (type === 'max' && height > limitHeight) {
							errors.push(
								`이미지 세로 사이즈가 ${limitHeight}px 보다 큽니다.`
							);
						}
					}

					if (type === 'ratio') {
						const ratio = width / height;
						const defaultRatio = widthRatio / heightRatio;
						if (defaultRatio !== ratio) {
							errors.push(
								`이미지 비율이 ${widthRatio} : ${heightRatio} 로 맞지 않습니다.`
							);
						}
					}
				}

				if (errors.length > 0) {
					this.fireError(errors);
				} else if (this.preSign && current < this.preSignExpireAt) {
					this.uploadImageWithSign().then(() => {
						this.showPreviewImage(width, height);
						this.emitUploadPath();
					});
				} else {
					this.resetPreSign();
					this.uploadImage().then(() => {
						this.showPreviewImage(width, height);
						this.emitUploadPath();
					});
				}
			},
			setImageMeta(event) {
				const { naturalWidth, naturalHeight } = event.target;

				this.showPreviewImage(naturalWidth, naturalHeight);
			},
			uploadImage() {
				spinnerActive();
				return imageUpload(
					this.previewImage.extension,
					this.group,
					this.previewImage.fileObject
				)
					.then(res => {
						this.requestUrl = res.config.url;
						this.preSign = res.config.data;
						this.preSignExpireAt =
							new Date().getTime() + 9 * 60 * 1000;

						spinnerInactive();
					})
					.catch(err => {
						spinnerInactive();
						this.fireError('파일 업로드 중 에러가 발생했습니다.');
						console.error(err);
					});
			},
			uploadImageWithSign() {
				spinnerActive();
				return imageUploadWithSign(
					this.requestUrl,
					this.preSign,
					this.previewImage.fileObject
				)
					.then(() => {
						spinnerInactive();
					})
					.catch(err => {
						spinnerInactive();
						this.fireError('파일 업로드 중 에러가 발생했습니다.');
						console.error(err);
					});
			},
			showPreviewImage(width, height) {
				this.showImage = true;
				this.previewImage.originalWidth = width;
				this.previewImage.originalHeight = height;
			},
			resetPreSign() {
				this.requestUrl = null;
				this.preSign = null;
				this.preSignExpireAt = null;
			},
			emitUploadPath() {
				const filePath = this.preSign.get('key').split('/');
				const fileSaveName = filePath.pop();
				let fileDir = `${filePath.join('/')}`;

				if (this.filePathHasSlash) {
					fileDir = '/' + fileDir;
				}

				this.key = `${fileDir}/${fileSaveName}`;

				const imageDto = {
					fileDir,
					fileSaveName,
					fileSize: this.previewImage.size,
					fileType: this.previewImage.fileType,
					fileOrgName: this.previewImage.fileName,
					path: '/' + this.preSign.get('key'),
				};

				this.$emit('upload', imageDto);
			},
			fireError(errors) {
				alert(Array.isArray(errors) ? errors.join('\n') : errors);
				this.cancelImage();
			},
		},
	};
</script>
<style lang="scss" scoped>
	DIV.uploader {
		display: inline-block;
		position: relative;

		.fade-enter-active,
		.fade-leave-active {
			transition: opacity 500ms ease;
		}

		.fade-enter,
		.fade-leave-to {
			opacity: 0;
		}

		.fade-leave,
		.fade-enter-to {
			opacity: 1;
		}

		.uploader__upload-button {
			position: relative;
			border: 1px solid $btn-blue;
			border-radius: 1em;

			&:after {
				content: '';
				position: absolute;
				display: block;
				width: 24px;
				height: 24px;
				border: 1px solid $btn-blue;
				border-radius: 50%;
				left: 50%;
				top: 50%;
				transform: translate3d(-50%, -50%, 0);
			}

			&:before {
				content: '';
				position: absolute;
				display: block;
				width: 3px;
				height: 3px;
				background: $btn-blue;
				left: 50%;
				top: 50%;
				transform: translate3d(-50%, -50%, 0);
				box-shadow:
					0 3px 0 0 $btn-blue,
					3px 0 0 $btn-blue,
					0 -3px 0 0 $btn-blue,
					-3px 0 0 0 $btn-blue;
			}
		}

		.uploader__input {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			opacity: 0;
			z-index: 99;
			cursor: pointer;
		}

		.uploader__preview {
			position: relative;
			border: 1px solid $btn-blue;
			border-radius: 1em;
			overflow: hidden;
			cursor: pointer;
		}

		.uploader__preview__img {
			position: absolute;
			width: 100%;
			top: 50%;
			left: 0;
			transform: translate3d(0, -50%, 0);
		}

		.uploader__preview__cancel {
			display: block;
			position: absolute;
			background: red;
			color: #fff;
			font-weight: bold;
			font-size: 1rem;
			padding: 0.5em 1em;
			border-radius: 1em;
			bottom: 0.5em;
			left: 50%;
			transform: translate3D(-50%, 0, 0);
			cursor: pointer;
		}

		.origin {
			position: fixed;
			width: 100%;
			height: 100%;
			top: 0;
			left: 0;
			z-index: 9999;
		}

		.origin__background {
			position: absolute;
			width: 100%;
			height: 100%;
			background: rgba(0, 0, 0, 0.75);
			cursor: pointer;
		}

		.origin__image-wrapper {
			position: absolute;
			left: 50%;
			top: 50%;
			transform: translate3d(-50%, -50%, 0);
			overflow: scroll;
		}

		.origin__image {
			width: 100%;
		}
	}
</style>
